import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RequestStatus, REQUEST_STATUSES } from 'stores/common';
import type { RootState } from 'stores/store';
import { getDoctor, getHubspotUser, patchDoctor } from './userClient';
import type { HubspotToken, User } from './userTypes';

export interface UserState {
  user?: User;
  hubspotToken?: HubspotToken,
  userRequestStatus?: RequestStatus;
  patchUserRequestStatus?: RequestStatus;
  hubspotRequestStatus?: RequestStatus;
}

const initialState: UserState = {
};

export const getUserAsync = createAsyncThunk('user/get', () => getDoctor());
export const getUserHubspotAsync = createAsyncThunk('user/getHubspot', (
  { email, firstName, lastName }: {email: string, firstName: string, lastName: string},
) => getHubspotUser(email, firstName, lastName));

export interface IUpdateUserAsync {
  user: User;
  ribFile?: File | null;
  signatureFile?: File | null;
}
export const updateUserAsync = createAsyncThunk(
  'user/patch',
  (params: IUpdateUserAsync) => patchDoctor(params),
);

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    resetUser: (state) => {
      delete state.user;
      delete state.userRequestStatus;
      delete state.patchUserRequestStatus;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserAsync.pending, (state) => {
        state.userRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(getUserAsync.fulfilled, (state, { payload }) => {
        state.userRequestStatus = REQUEST_STATUSES.SUCCESS;
        state.user = payload;
      })
      .addCase(getUserAsync.rejected, (state) => {
        state.userRequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(updateUserAsync.pending, (state) => {
        state.patchUserRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(updateUserAsync.fulfilled, (state, { payload }) => {
        state.patchUserRequestStatus = REQUEST_STATUSES.SUCCESS;
        state.user = payload;
      })
      .addCase(updateUserAsync.rejected, (state) => {
        state.patchUserRequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(getUserHubspotAsync.pending, (state) => {
        state.hubspotRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(getUserHubspotAsync.fulfilled, (state, { payload }) => {
        state.hubspotRequestStatus = REQUEST_STATUSES.SUCCESS;
        state.hubspotToken = payload;
      })
      .addCase(getUserHubspotAsync.rejected, (state) => {
        state.hubspotRequestStatus = REQUEST_STATUSES.ERROR;
      });
  },
});

export const { resetUser } = userSlice.actions;
export const getUser = (state: RootState) => state.user.user;
export const getHubspotToken = (state: RootState) => state.user.hubspotToken;
export const getUserRequestStatus = (state: RootState) => state.user.userRequestStatus;
export const getHubspotRequestStatus = (state: RootState) => state.user.hubspotRequestStatus;
export const getPatchUserRequestStatus = (state: RootState) => state.user.patchUserRequestStatus;

export default userSlice.reducer;
